import React, { memo } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { 
  ChevronDown, 
  ChevronUp, 
  Clock, 
  BarChart, 
  LineChart, 
  TrendingUp, 
  AlertTriangle, 
  CheckCircle 
} from 'lucide-react';
import { MarketAlert } from '@/utils/api';

interface MarketAlertCardProps {
  alert: MarketAlert;
  isExpanded: boolean;
  toggleExpand: (id: number) => void;
}

// Utility functions for badge colors
const getRiskLevelColor = (level: string) => {
  switch (level.toUpperCase()) {
    case 'HIGH':
      return 'bg-red-500/20 text-red-400 border-red-500/30';
    case 'MEDIUM':
      return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    case 'LOW':
      return 'bg-green-500/20 text-green-400 border-green-500/30';
    default:
      return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
  }
};

const MarketAlertCard = memo(({ alert, isExpanded, toggleExpand }: MarketAlertCardProps) => {
  const riskClass = getRiskLevelColor(alert.risk_level);
  
  // Function to safely render HTML content
  const createMarkup = (content: string) => {
    return { __html: content };
  };
  
  return (
    <Card 
      key={`market-${alert.id}`}
      className={cn(
        "overflow-hidden transition-all duration-300 border border-white/10",
        "bg-gradient-to-b from-sidebar/80 to-sidebar backdrop-blur-sm relative",
        "hover:shadow-md hover:shadow-green-900/20 group"
      )}
    >
      <div className={`absolute top-0 left-0 w-1 h-full ${riskClass}`}></div>
      <div 
        className="p-4 cursor-pointer" 
        onClick={() => toggleExpand(alert.id)}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3">
            <div className="p-2 rounded-full bg-green-500/10 text-green-400">
              <BarChart className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-white">Strykr Market Alert</h3>
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                  MARKET
                </Badge>
              </div>
              <div 
                className="mt-1 text-white/90 text-left"
                dangerouslySetInnerHTML={{ __html: alert.short_summary }}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={getRiskLevelColor(alert.risk_level)}>
              {alert.risk_level}
            </Badge>
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </div>
        </div>
        <div className="flex items-center mt-3 text-sm text-white/60">
          <Clock className="h-3.5 w-3.5 mr-1.5" />
          {formatDistanceToNow(new Date(alert.timestamp), { addSuffix: true })}
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 pt-2 border-t border-white/10">
          <div className="bg-black/30 rounded-lg p-4">
            <h4 className="font-medium text-white mb-2">Full Analysis</h4>
            <div 
              className="text-white/80 text-sm max-w-none text-left"
              style={{ lineHeight: '1.4', wordBreak: 'break-word', textAlign: 'left' }}
              dangerouslySetInnerHTML={{ __html: alert.full_analysis.extended_summary }}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              <div>
                <h5 className="text-xs font-medium text-white/60 mb-2 flex items-center">
                  <LineChart className="h-3.5 w-3.5 mr-1.5 text-blue-400" />
                  Market Conditions
                </h5>
                <div 
                  className="text-white/80 text-sm max-w-none text-left"
                  style={{ lineHeight: '1.4', wordBreak: 'break-word', textAlign: 'left' }}
                  dangerouslySetInnerHTML={{ __html: alert.full_analysis.market_conditions }}
                />
              </div>
              <div>
                <h5 className="text-xs font-medium text-white/60 mb-2 flex items-center">
                  <TrendingUp className="h-3.5 w-3.5 mr-1.5 text-blue-400" />
                  Key Movements
                </h5>
                <ul className="text-sm text-white/80 space-y-2 pl-0 text-left">
                  {alert.full_analysis.key_movements?.map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-blue-400 mr-2 min-w-[12px] text-left inline-block">•</span>
                      <div className="flex-1 text-left" dangerouslySetInnerHTML={{ __html: item }} />
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h5 className="text-xs font-medium text-white/60 mb-2 flex items-center">
                  <AlertTriangle className="h-3.5 w-3.5 mr-1.5 text-red-400" />
                  Risk Factors
                </h5>
                <ul className="text-sm text-white/80 space-y-2 pl-0 text-left">
                  {alert.full_analysis.risk_factors?.map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-red-400 mr-2 min-w-[12px] text-left inline-block">•</span>
                      <div className="flex-1 text-left" dangerouslySetInnerHTML={{ __html: item }} />
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h5 className="text-xs font-medium text-white/60 mb-2 flex items-center">
                  <CheckCircle className="h-3.5 w-3.5 mr-1.5 text-green-400" />
                  Opportunities
                </h5>
                <ul className="text-sm text-white/80 space-y-2 pl-0 text-left">
                  {alert.full_analysis.opportunities?.map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-green-400 mr-2 min-w-[12px] text-left inline-block">•</span>
                      <div className="flex-1 text-left" dangerouslySetInnerHTML={{ __html: item }} />
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
});

MarketAlertCard.displayName = 'MarketAlertCard';

export default MarketAlertCard;
